// journalTransform.ts — freee 単式 deal → 複式簿記(借方/貸方) 変換 (仕訳レビュー用, 2026-07-02)
//
// アルゴリズム根拠 (worker.js 実証事実 / reader_a 調査):
//   - freee は全 detail を deal type の主側に計上する (expense=借方 / income=貸方)。
//     逆側(contra)行は「主側 + 負の金額」として保存される (worker L17268-17282,
//     2026-06-11 incident で実証)。→ amount<0 の detail は反対側へ反転する。
//   - 反対側は payments[](決済済 = 口座) + 未決済残 due_amount(経過勘定: 未払金/売掛金 相当)。
//   - due_amount>0 かつ status=settled の「偽残」pattern が freee 側にある (GATE_68b)
//     → balanced=false でも warn 表示に留めエラー化しない。
//   - 名称は Map 引きのみ (勘定/税区分名のハードコード禁止 — masters cache が SSOT)。

import type { JournalReviewDeal, JournalReviewDealDetail } from '../api/client';

export interface EntryCellData {
  kind: 'detail' | 'payment' | 'accrual';
  accountName: string;
  amount: number;            // 表示金額 (絶対値・税込)
  taxLabel?: string;         // detail のみ (課対仕入10% 等)
  vat?: number | null;
  sectionName?: string;
  partnerName?: string;
  itemName?: string;
  tagNames?: string[];
  description?: string;
  paymentDate?: string;      // payment のみ
}

export interface LedgerLineData {
  no: number;
  debit: EntryCellData | null;
  credit: EntryCellData | null;
}

export interface JournalGroupData {
  id: number;
  refNo: string;             // 'DL-3613589904'
  refNumber: string | null;  // freee ref_number (arica:xxx 等)
  title: string;             // 取引先名 or 主勘定
  summaryText: string;       // 部門・勘定の要約
  type: string;              // expense / income / ...
  issueDate: string;
  dueDate: string | null;
  firstSeenAt: string | null;   // FREEEMAN初回把握 (近似フォールバック)
  lastSeenAt: string | null;    // FREEEMAN変化検知 (近似フォールバック)
  createdAt: string | null;     // 有効登録日時 = freee真値(仕訳帳) 優先、無ければ近似
  updatedAt: string | null;     // 有効更新日時 = 同上
  tsSource: 'freee' | 'approx'; // createdAt/updatedAt の出所
  settled: boolean;
  amount: number;
  debitTotal: number;
  creditTotal: number;
  balanced: boolean;
  lines: LedgerLineData[];
  detailFetched: boolean;    // 単体GET済み (description 取得済) か
}

export interface NameMaps {
  accounts: Map<number, string>;
  sections: Map<number, string>;
  taxCodes: Map<number, string>;
  items: Map<number, string>;
  tags: Map<number, string>;
  partners: Map<number, string>;
  wallets: Map<string, string>; // `${type}|${id}` → 口座名
}

const nameOr = (m: Map<number, string>, id: number | null | undefined, fb: string): string =>
  (id != null && m.get(Number(id))) || fb;

function detailToCell(d: JournalReviewDealDetail, maps: NameMaps, partnerName: string): EntryCellData {
  return {
    kind: 'detail',
    accountName: nameOr(maps.accounts, d.account_item_id, `勘定#${d.account_item_id}`),
    amount: Math.abs(d.amount),
    taxLabel: nameOr(maps.taxCodes, d.tax_code, `税区分#${d.tax_code}`),
    vat: d.vat,
    sectionName: d.section_id != null ? nameOr(maps.sections, d.section_id, `部門#${d.section_id}`) : undefined,
    partnerName: partnerName || undefined,
    itemName: d.item_id != null ? nameOr(maps.items, d.item_id, `品目#${d.item_id}`) : undefined,
    tagNames: (d.tag_ids || []).map((t) => nameOr(maps.tags, t, `タグ#${t}`)),
    description: d.description || undefined,
  };
}

export function dealToJournalGroup(
  deal: JournalReviewDeal,
  maps: NameMaps,
  opts?: { detailFetched?: boolean },
): JournalGroupData {
  const mainSide: 'debit' | 'credit' = deal.type === 'income' ? 'credit' : 'debit';
  const partnerName = deal.partner_id != null
    ? nameOr(maps.partners, deal.partner_id, `取引先#${deal.partner_id}`)
    : '';

  const debit: EntryCellData[] = [];
  const credit: EntryCellData[] = [];

  // (a) details → 主側 (負額 detail は逆側へ反転)
  for (const d of deal.details || []) {
    let side: 'debit' | 'credit' = mainSide;
    if (d.amount < 0) side = mainSide === 'debit' ? 'credit' : 'debit';
    else if (d.entry_side && d.entry_side !== mainSide) side = d.entry_side; // 防御 (古い deal)
    (side === 'debit' ? debit : credit).push(detailToCell(d, maps, partnerName));
  }

  // (b) payments → 反対側 (決済済 = 口座名)
  for (const p of deal.payments || []) {
    const cell: EntryCellData = {
      kind: 'payment',
      accountName: maps.wallets.get(`${p.from_walletable_type}|${p.from_walletable_id}`)
        || (p.from_walletable_type === 'bank_account' ? `口座#${p.from_walletable_id}` : '現金・その他'),
      amount: Math.abs(p.amount),
      paymentDate: p.date,
      description: `決済 ${p.date}`,
    };
    (deal.type === 'income' ? debit : credit).push(cell);
  }

  // (c) 未決済残 → 反対側 (経過勘定。勘定断定はせず汎用表記)
  const remaining = deal.due_amount != null
    ? Number(deal.due_amount)
    : (deal.status === 'unsettled' ? Number(deal.amount) : 0);
  if (remaining > 0) {
    const cell: EntryCellData = {
      kind: 'accrual',
      accountName: deal.type === 'income' ? '未収入金・売掛金(未決済)' : '未払金(未決済)',
      amount: remaining,
      description: deal.due_date ? `支払期日 ${deal.due_date}` : undefined,
    };
    (deal.type === 'income' ? debit : credit).push(cell);
  }

  const debitTotal = debit.reduce((s, c) => s + c.amount, 0);
  const creditTotal = credit.reduce((s, c) => s + c.amount, 0);

  // (e) 借方行と貸方行を index 対置、少ない側は blank(null)
  const n = Math.max(debit.length, credit.length, 1);
  const lines: LedgerLineData[] = [];
  for (let i = 0; i < n; i++) {
    lines.push({ no: i + 1, debit: debit[i] || null, credit: credit[i] || null });
  }

  // タイトル/要約 (list mode に partner_name が無いため map 引きの名称を使う)
  const accountNames = Array.from(new Set(
    (deal.details || []).map((d) => nameOr(maps.accounts, d.account_item_id, `#${d.account_item_id}`)),
  ));
  const sectionNames = Array.from(new Set(
    (deal.details || []).filter((d) => d.section_id != null)
      .map((d) => nameOr(maps.sections, d.section_id, `#${d.section_id}`)),
  ));
  const title = partnerName || accountNames[0] || `取引 ${deal.id}`;
  const summaryParts: string[] = [];
  if (accountNames.length) summaryParts.push(accountNames.slice(0, 3).join('・') + (accountNames.length > 3 ? ` 他${accountNames.length - 3}科目` : ''));
  if (sectionNames.length) summaryParts.push(sectionNames.slice(0, 3).join('・') + (sectionNames.length > 3 ? ` 他${sectionNames.length - 3}部門` : ''));
  if (deal.ref_number) summaryParts.push(deal.ref_number);

  return {
    id: deal.id,
    refNo: `DL-${deal.id}`,
    refNumber: deal.ref_number || null,
    title,
    summaryText: summaryParts.join(' ／ ') || '(明細なし)',
    type: deal.type,
    issueDate: deal.issue_date,
    dueDate: deal.due_date || null,
    firstSeenAt: deal.first_seen_at || null,
    lastSeenAt: deal.last_seen_at || null,
    createdAt: deal.freee_created_at || deal.first_seen_at || null,
    updatedAt: deal.freee_updated_at || deal.last_seen_at || null,
    tsSource: deal.freee_created_at ? 'freee' : 'approx',
    settled: deal.status === 'settled',
    amount: Number(deal.amount) || 0,
    debitTotal,
    creditTotal,
    balanced: debitTotal === creditTotal,
    lines,
    detailFetched: !!(opts && opts.detailFetched),
  };
}
